/**
 * The arithmetic behind the heatmap maker: reading a value onto a colour ramp.
 *
 * Three ways to colour a table:
 *
 *   whole table   one scale for every cell, so the same colour always means the same
 *                 number. A sequential ramp runs from the low end to the high end of the
 *                 data (or of the ends the reader pins). A diverging ramp has a
 *                 midpoint — zero by default when the data cross zero — drawn in the
 *                 ramp's pale centre, with the two sides scaled separately when the ends
 *                 are pinned unevenly (the "two-slope" rule matplotlib calls TwoSlopeNorm).
 *   each row      every row scaled from its own minimum to its own maximum. Useful when
 *                 rows are in different units; the colour then shows rank within the row,
 *                 not size across the table.
 *   each column   the same, down each column.
 *
 * Colour is interpolated linearly between a ramp's stops in sRGB, which is how
 * ColorBrewer's own ramps are usually extended; viridis and cividis have 256 stops, so
 * the interpolation there only reaches between neighbouring samples.
 */

import { extent, niceTicks } from "@/lib/tool-kit/charts/scale";
import {
  HEATMAP_PALETTES,
  type HeatmapPalette,
  type HeatmapPaletteId,
} from "@/lib/tool-kit/color-charts/heatmap-palettes";

export type ColorBy = "table" | "row" | "column";

export function paletteById(id: HeatmapPaletteId): HeatmapPalette {
  return HEATMAP_PALETTES.find((p) => p.id === id) ?? HEATMAP_PALETTES[0];
}

function hexChannel(hex: string, offset: number): number {
  return Number.parseInt(hex.slice(offset, offset + 2), 16);
}

function toHex(r: number, g: number, b: number): string {
  const part = (value: number) =>
    Math.round(value).toString(16).padStart(2, "0");
  return `#${part(r)}${part(g)}${part(b)}`;
}

/** The colour at position `t` (0–1) along a ramp, optionally run backwards. */
export function colorAt(
  palette: HeatmapPalette,
  t: number,
  reverse = false
): string {
  const clamped = Math.min(1, Math.max(0, t));
  const position =
    (reverse ? 1 - clamped : clamped) * (palette.stops.length - 1);
  const low = Math.floor(position);
  const high = Math.min(palette.stops.length - 1, low + 1);
  const a = palette.stops[low];
  const b = palette.stops[high];
  const mix = position - low;
  const blend = (offset: number) =>
    hexChannel(a, offset) +
    (hexChannel(b, offset) - hexChannel(a, offset)) * mix;
  return toHex(blend(1), blend(3), blend(5));
}

export interface HeatmapScale {
  low: number;
  /** The value drawn in the centre of a diverging ramp; null for a sequential one. */
  mid: number | null;
  high: number;
  ticks: number[];
  decimals: number;
  /** True when pinned values were unusable and the automatic scale was used instead. */
  bad: boolean;
}

export interface ScalePins {
  low: number | null;
  mid: number | null;
  high: number | null;
}

function median(values: readonly number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1
    ? sorted[middle]
    : (sorted[middle - 1] + sorted[middle]) / 2;
}

/** Zero when the data cross it, otherwise the median — a midpoint that is in the data. */
export function autoMidpoint(values: readonly number[]): number {
  const [low, high] = extent(values) ?? [0, 1];
  return low < 0 && high > 0 ? 0 : median(values.length > 0 ? values : [0]);
}

function finishScale(
  low: number,
  mid: number | null,
  high: number,
  bad: boolean
): HeatmapScale {
  const { ticks, decimals } = niceTicks(low, high, { exact: true });
  return { low, mid, high, ticks, decimals, bad };
}

function sequentialScale(
  values: readonly number[],
  pins: ScalePins
): HeatmapScale {
  const [dataLow, dataHigh] = extent(values) ?? [0, 1];
  const autoLow = dataLow === dataHigh ? dataLow - 1 : dataLow;
  const autoHigh = dataLow === dataHigh ? dataHigh + 1 : dataHigh;
  const low = pins.low ?? autoLow;
  const high = pins.high ?? autoHigh;
  if (low < high) {
    return finishScale(low, null, high, false);
  }
  return finishScale(autoLow, null, autoHigh, true);
}

function divergingScale(
  values: readonly number[],
  pins: ScalePins
): HeatmapScale {
  const [dataLow, dataHigh] = extent(values) ?? [0, 1];
  const mid = pins.mid ?? autoMidpoint(values);
  const reach = Math.max(dataHigh - mid, mid - dataLow, 0) || 1;
  const symmetric = { low: mid - reach, high: mid + reach };
  const low = pins.low ?? symmetric.low;
  const high = pins.high ?? symmetric.high;
  if (low < mid && mid < high) {
    return finishScale(low, mid, high, false);
  }
  const pinned = pins.low !== null || pins.high !== null || pins.mid !== null;
  return finishScale(symmetric.low, mid, symmetric.high, pinned);
}

export function heatmapScale(
  values: readonly number[],
  diverging: boolean,
  pins: ScalePins
): HeatmapScale {
  return diverging
    ? divergingScale(values, pins)
    : sequentialScale(values, pins);
}

/** Position 0–1 on the ramp for `value`, clamped to the ends. */
export function scaleFraction(value: number, scale: HeatmapScale): number {
  const { low, mid, high } = scale;
  let raw: number;
  if (mid === null) {
    raw = (value - low) / (high - low);
  } else if (value >= mid) {
    raw = 0.5 + (0.5 * (value - mid)) / (high - mid);
  } else {
    raw = 0.5 - (0.5 * (mid - value)) / (mid - low);
  }
  return Math.min(1, Math.max(0, raw));
}

/** Min–max position of every cell within its own row or column; 0.5 where it is flat. */
export function groupFractions(
  grid: readonly (readonly (number | null)[])[],
  by: "row" | "column"
): (number | null)[][] {
  const columns = Math.max(0, ...grid.map((row) => row.length));
  const groups: (number | null)[][] =
    by === "row"
      ? grid.map((row) => [...row])
      : Array.from({ length: columns }, (_, c) =>
          grid.map((row) => row[c] ?? null)
        );
  const scaled = groups.map((group) => {
    const range = extent(group);
    return group.map((value) => {
      if (value === null || range === null) {
        return null;
      }
      const [low, high] = range;
      return high === low ? 0.5 : (value - low) / (high - low);
    });
  });
  if (by === "row") {
    return scaled;
  }
  return grid.map((row, r) => row.map((_, c) => scaled[c]?.[r] ?? null));
}

/** The ramp position of every cell, for whichever way of colouring was chosen. */
export function cellFractions(
  grid: readonly (readonly (number | null)[])[],
  scale: HeatmapScale,
  by: ColorBy
): (number | null)[][] {
  if (by !== "table") {
    return groupFractions(grid, by);
  }
  return grid.map((row) =>
    row.map((value) => (value === null ? null : scaleFraction(value, scale)))
  );
}

/** Every finite value in the grid. */
export function gridValues(
  grid: readonly (readonly (number | null)[])[]
): number[] {
  return grid.flat().filter((v): v is number => v !== null);
}
